import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  InputAdornment,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { visuallyHidden } from "@mui/utils";
import dayjs, { Dayjs } from "dayjs";
import {
  ClioService,
  LloFunnelReportItem,
  LloFunnelReportRequest,
  OpenAPI,
  UsersModel,
} from "../../../api";
import FilterableSelect from "../../../components/basic/FilterableSelect";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const MySwal = withReactContent(Swal);
const TEAM_MEMBER_ALL = "";
const COLUMN_COUNT = 12;

type SortableColumn =
  | "caseName"
  | "raa"
  | "ap"
  | "upcomingDeadlines"
  | "lloExpiration"
  | "negotiation"
  | "funnel"
  | "dueDate"
  | "taskName"
  | "taskAssignee"
  | "description";

type SortDirection = "asc" | "desc";

type RowWithSpans = LloFunnelReportItem & {
  rowNumber?: number;
  groupRowSpan?: number;
  skipCaseColumns?: boolean;
};

const UPCOMING_DEADLINES_WIDTH = 155;

const HEAD_CELLS: {
  id: SortableColumn;
  label: string;
  width?: number;
  minWidth?: number;
  flex?: boolean;
}[] = [
  { id: "caseName", label: "Case Name", width: 150 },
  { id: "raa", label: "RAA", width: 68 },
  { id: "ap", label: "AP", width: 68 },
  { id: "upcomingDeadlines", label: "Upcoming Deadlines", width: UPCOMING_DEADLINES_WIDTH },
  { id: "lloExpiration", label: "LLO Expiration", width: 98 },
  { id: "negotiation", label: "Negotiation", width: 78 },
  { id: "funnel", label: "Funnel", width: 230 },
  { id: "dueDate", label: "Due Date", width: 92 },
  { id: "taskName", label: "Task", width: 190 },
  { id: "taskAssignee", label: "Task Assignee", width: 96 },
  { id: "description", label: "Description", minWidth: 360, flex: true },
];

const TABLE_HEADER_CELL_SX = {
  fontWeight: 600,
  fontSize: "0.8125rem",
  backgroundColor: "#6b7280",
  color: "#fff",
  borderBottom: "none",
  whiteSpace: "nowrap" as const,
  py: 1.25,
  "& .MuiTableSortLabel-root": { color: "rgba(255,255,255,0.85)" },
  "& .MuiTableSortLabel-root:hover": { color: "#fff" },
  "& .MuiTableSortLabel-root.Mui-active": { color: "#fff" },
  "& .MuiTableSortLabel-icon": { color: "#fff !important" },
};

const TABLE_BODY_CELL_SX = {
  fontSize: "0.8125rem",
  py: 1.25,
  borderColor: "divider",
  verticalAlign: "top" as const,
};

function getHeadCellSx(headCell: (typeof HEAD_CELLS)[number]) {
  if (headCell.flex) {
    return { ...TABLE_HEADER_CELL_SX, minWidth: headCell.minWidth, width: "auto" };
  }

  return {
    ...TABLE_HEADER_CELL_SX,
    width: headCell.width,
    minWidth: headCell.width,
    maxWidth: headCell.width,
  };
}

function getBodyCellSx(
  headCell: (typeof HEAD_CELLS)[number],
  extra: Record<string, unknown> = {}
) {
  if (headCell.flex) {
    return { ...TABLE_BODY_CELL_SX, minWidth: headCell.minWidth, ...extra };
  }

  return {
    ...TABLE_BODY_CELL_SX,
    width: headCell.width,
    minWidth: headCell.width,
    maxWidth: headCell.width,
    ...extra,
  };
}

const HEAD_CELL_BY_ID = Object.fromEntries(HEAD_CELLS.map((cell) => [cell.id, cell])) as Record<
  SortableColumn,
  (typeof HEAD_CELLS)[number]
>;

const matterGroupCellStyle = {
  borderBottom: "2px solid",
  borderColor: "divider",
  verticalAlign: "top" as const,
};

const OVERDUE_ROW_BG = "#fdecea";
const OVERDUE_ROW_HOVER_BG = "#f9d5d2";

function getRowCellSx(overdue: boolean) {
  return {
    ...TABLE_BODY_CELL_SX,
    ...(overdue ? { bgcolor: OVERDUE_ROW_BG } : {}),
  };
}

function normalizeText(value?: string | null) {
  if (!value) return "";
  return value.replace(/\r\n|\n\r|\r/g, "\n");
}

function containsHtml(value: string) {
  return /<[a-z][\s\S]*>/i.test(value);
}

function stripHtml(value: string) {
  return value.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function sanitizeTaskDescriptionHtml(html: string) {
  const doc = new DOMParser().parseFromString(html, "text/html");

  doc.querySelectorAll("script, style, iframe, object, embed, form").forEach((el) => el.remove());

  doc.body.querySelectorAll("*").forEach((el) => {
    [...el.attributes].forEach((attr) => {
      const name = attr.name.toLowerCase();
      if (name.startsWith("on")) {
        el.removeAttribute(attr.name);
      }
    });

    if (el.tagName === "A") {
      el.setAttribute("target", "_blank");
      el.setAttribute("rel", "noopener noreferrer");
    }
  });

  return doc.body.innerHTML;
}

const descriptionContentSx = {
  whiteSpace: "pre-wrap" as const,
  wordBreak: "break-word" as const,
  "& a": {
    color: "primary.main",
    textDecoration: "underline",
  },
  "& p": { margin: 0 },
  "& ul, & ol": { margin: 0, paddingLeft: 2 },
};

function DescriptionCell({ text }: { text?: string | null }) {
  const content = text ?? "";
  if (!content.trim()) {
    return <>—</>;
  }

  if (!containsHtml(content)) {
    return <Box sx={descriptionContentSx}>{content}</Box>;
  }

  return (
    <Box
      sx={descriptionContentSx}
      dangerouslySetInnerHTML={{ __html: sanitizeTaskDescriptionHtml(content) }}
    />
  );
}

function getFunnelCategoryOrder(funnel?: string | null): number {
  const value = funnel ?? "";
  if (value.startsWith("Main Funnel")) return 0;
  if (value.startsWith("Secondary Funnel")) return 1;
  if (value.startsWith("Single Task")) return 2;
  if (value === "Manual") return 3;
  return 4;
}

function getSortValue(row: LloFunnelReportItem, column: SortableColumn): string | number {
  switch (column) {
    case "caseName":
      return row.caseName?.toLowerCase() ?? "";
    case "raa":
      return row.raa?.toLowerCase() ?? "";
    case "ap":
      return row.ap?.toLowerCase() ?? "";
    case "upcomingDeadlines":
      return row.upcomingDeadlines?.toLowerCase() ?? "";
    case "lloExpiration":
      return row.lloExpiration?.toLowerCase() ?? "";
    case "negotiation":
      return row.negotiation?.toLowerCase() ?? "";
    case "funnel":
      return `${getFunnelCategoryOrder(row.funnel)}|${row.funnel?.toLowerCase() ?? ""}`;
    case "taskName":
      return row.taskName?.toLowerCase() ?? "";
    case "taskAssignee":
      return row.taskAssignee?.toLowerCase() ?? "";
    case "description":
      return stripHtml(row.description ?? "").toLowerCase();
    case "dueDate":
      return row.dueDate ? dayjs(row.dueDate).valueOf() : Number.MAX_SAFE_INTEGER;
    default:
      return "";
  }
}

function compareRows(
  a: LloFunnelReportItem,
  b: LloFunnelReportItem,
  orderBy: SortableColumn,
  order: SortDirection
) {
  if ((a.caseName ?? "") !== (b.caseName ?? "")) {
    const aValue = getSortValue(a, orderBy);
    const bValue = getSortValue(b, orderBy);

    if (aValue < bValue) return order === "asc" ? -1 : 1;
    if (aValue > bValue) return order === "asc" ? 1 : -1;

    return (a.caseName ?? "").localeCompare(b.caseName ?? "");
  }

  const funnelOrder = getFunnelCategoryOrder(a.funnel) - getFunnelCategoryOrder(b.funnel);
  if (funnelOrder !== 0) return funnelOrder;

  const aValue = getSortValue(a, orderBy);
  const bValue = getSortValue(b, orderBy);

  if (aValue < bValue) return order === "asc" ? -1 : 1;
  if (aValue > bValue) return order === "asc" ? 1 : -1;

  const funnelNameCompare = (a.funnel ?? "").localeCompare(b.funnel ?? "");
  if (funnelNameCompare !== 0) return funnelNameCompare;

  const aDue = a.dueDate ? dayjs(a.dueDate).valueOf() : Number.MAX_SAFE_INTEGER;
  const bDue = b.dueDate ? dayjs(b.dueDate).valueOf() : Number.MAX_SAFE_INTEGER;
  return aDue - bDue;
}

function computeCaseGroupSpans(rows: LloFunnelReportItem[]): RowWithSpans[] {
  const out: RowWithSpans[] = [];
  let i = 0;
  let caseNumber = 0;

  while (i < rows.length) {
    const caseName = rows[i]?.caseName ?? "";
    let j = i + 1;
    while (j < rows.length && (rows[j]?.caseName ?? "") === caseName) j++;

    const span = j - i;
    caseNumber += 1;

    for (let k = i; k < j; k++) {
      out.push({
        ...rows[k],
        rowNumber: k === i ? caseNumber : undefined,
        groupRowSpan: k === i ? span : undefined,
        skipCaseColumns: k !== i,
      });
    }

    i = j;
  }

  return out;
}

function UpcomingDeadlinesCell({ text }: { text?: string | null }) {
  const [expanded, setExpanded] = useState(false);
  const [overflow, setOverflow] = useState(false);
  const raw = normalizeText(text);
  const isEmpty = !raw.trim() || raw.trim().toLowerCase() === "n/a";

  if (isEmpty) {
    return <>—</>;
  }

  return (
    <Box>
      <Box
        ref={(el: HTMLDivElement | null) => {
          if (!el || expanded) return;
          const isOverflowing = el.scrollHeight > el.clientHeight + 1;
          setOverflow((prev) => (prev === isOverflowing ? prev : isOverflowing));
        }}
        sx={
          expanded
            ? { whiteSpace: "pre-wrap", wordBreak: "break-word" }
            : {
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 3,
                overflow: "hidden",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }
        }
      >
        {raw}
      </Box>
      {overflow && (
        <Link
          component="button"
          type="button"
          variant="caption"
          onClick={() => setExpanded((prev) => !prev)}
          sx={{ mt: 0.5, display: "inline-block" }}
        >
          {expanded ? "Show less" : "Show more"}
        </Link>
      )}
    </Box>
  );
}

export default function LloFunnelReport() {
  const [reportData, setReportData] = useState<LloFunnelReportItem[]>([]);
  const [usersList, setUsersList] = useState<UsersModel[]>([]);
  const [teamMemberName, setTeamMemberName] = useState(TEAM_MEMBER_ALL);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState<SortDirection>("asc");
  const [orderBy, setOrderBy] = useState<SortableColumn>("caseName");

  useEffect(() => {
    ClioService.getApiClioClioUsers().then((users) => {
      setUsersList(users.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "")));
    });
  }, []);

  const buildRequest = useCallback((): LloFunnelReportRequest => {
    const selectedUser =
      teamMemberName && teamMemberName !== TEAM_MEMBER_ALL
        ? usersList.find((user) => user.name === teamMemberName)
        : null;

    return {
      teamMemberId: selectedUser?.id ?? null,
      teamMemberName: selectedUser?.name ?? null,
      startDate: startDate ? dayjs(startDate).format("YYYY-MM-DD") : null,
      endDate: endDate ? dayjs(endDate).format("YYYY-MM-DD") : null,
      searchTerm: searchTerm.trim() || null,
    };
  }, [endDate, searchTerm, startDate, teamMemberName, usersList]);

  const loadReport = useCallback(() => {
    setLoading(true);
    ClioService.postApiClioLloFunnelReport(buildRequest())
      .then((data) => {
        setReportData(data);
        setPage(0);
      })
      .catch(() =>
        MySwal.fire({
          icon: "error",
          title: <p>Error loading LLO funnel report.</p>,
        })
      )
      .finally(() => setLoading(false));
  }, [buildRequest]);

  const sortedData = useMemo(
    () => [...reportData].sort((a, b) => compareRows(a, b, orderBy, order)),
    [reportData, order, orderBy]
  );

  const caseGroups = useMemo(() => {
    const groups: LloFunnelReportItem[][] = [];
    let currentGroup: LloFunnelReportItem[] = [];
    let currentCaseName = "";

    sortedData.forEach((row) => {
      if (row.caseName !== currentCaseName) {
        if (currentGroup.length > 0) {
          groups.push(currentGroup);
        }
        currentGroup = [row];
        currentCaseName = row.caseName;
      } else {
        currentGroup.push(row);
      }
    });

    if (currentGroup.length > 0) {
      groups.push(currentGroup);
    }

    return groups;
  }, [sortedData]);

  const paginatedRows = useMemo(() => {
    const pageGroups = caseGroups.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    return computeCaseGroupSpans(pageGroups.flat());
  }, [caseGroups, page, rowsPerPage]);

  const uniqueCaseCount = caseGroups.length;

  const handleRequestSort = (column: SortableColumn) => {
    const isAsc = orderBy === column && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(column);
    setPage(0);
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await fetch(`${OpenAPI.BASE}/api/Clio/lloFunnelReport/download`, {
        method: "POST",
        headers: {
          Authorization: (OpenAPI.HEADERS as Record<string, string>)?.Authorization,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(buildRequest()),
      });

      if (!response.ok) {
        throw new Error("Download failed");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = `LloFunnelReport-${dayjs().format("YYYY-MM-DD")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch {
      MySwal.fire({
        icon: "error",
        title: <p>Error downloading LLO funnel report.</p>,
      });
    } finally {
      setDownloading(false);
    }
  };

  const isOverdue = (dueDate?: string | null) =>
    !!dueDate && dayjs(dueDate).isBefore(dayjs(), "day");

  return (
    <Box
      sx={{
        height: "calc(100vh - 120px)",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          flexShrink: 0,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={600} lineHeight={1.3}>
            LLO Case Funnel Overview
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Limited Litigation tasks with descriptions modified from the standard template.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={downloading ? <CircularProgress size={16} /> : <FileDownloadIcon />}
          onClick={handleDownload}
          disabled={downloading || loading || reportData.length === 0}
        >
          Download
        </Button>
      </Box>

      <Paper variant="outlined" sx={{ px: 2, py: 1.5, flexShrink: 0, bgcolor: "grey.50" }}>
        <Box display="flex" flexWrap="wrap" gap={1.5} alignItems="center">
          <FilterableSelect
            label="Team Member"
            value={teamMemberName || TEAM_MEMBER_ALL}
            options={usersList.map((user) => ({
              value: user.name!,
              label: user.name!,
            }))}
            onChange={(value) =>
              setTeamMemberName(
                value === undefined || value === TEAM_MEMBER_ALL ? TEAM_MEMBER_ALL : String(value)
              )
            }
            allOptionLabel="Select None"
            allOptionValue={TEAM_MEMBER_ALL}
            searchPlaceholder="Filter team members..."
            minWidth={220}
          />
          <DatePicker
            label="Task Due Date From"
            value={startDate}
            onChange={(value) => setStartDate(value)}
            slotProps={{ textField: { size: "small", sx: { width: 200 } } }}
          />
          <DatePicker
            label="Task Due Date To"
            value={endDate}
            onChange={(value) => setEndDate(value)}
            slotProps={{ textField: { size: "small", sx: { width: 200 } } }}
          />
          <TextField
            placeholder="Search..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: "100%", sm: 260 }, flexGrow: { sm: 1 }, maxWidth: 320 }}
          />
          <Button variant="contained" size="small" onClick={loadReport} disabled={loading}>
            {loading ? <CircularProgress size={18} color="inherit" /> : "Load Report"}
          </Button>
        </Box>
      </Paper>

      <Paper
        variant="outlined"
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {reportData.length > 0 && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 1, display: "block" }}>
              {uniqueCaseCount} active case{uniqueCaseCount === 1 ? "" : "s"} · {reportData.length} task
              {reportData.length === 1 ? "" : "s"}
            </Typography>
            <Divider />
          </>
        )}

        <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
          <Table size="small" stickyHeader sx={{ tableLayout: "fixed", width: "100%" }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...TABLE_HEADER_CELL_SX, width: 44, minWidth: 44 }}>#</TableCell>
                {HEAD_CELLS.map((headCell) => (
                  <TableCell
                    key={headCell.id}
                    sortDirection={orderBy === headCell.id ? order : false}
                    sx={getHeadCellSx(headCell)}
                  >
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : "asc"}
                      onClick={() => handleRequestSort(headCell.id)}
                    >
                      {headCell.label}
                      {orderBy === headCell.id ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === "desc" ? "sorted descending" : "sorted ascending"}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 8 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : paginatedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 6, color: "text.secondary" }}>
                    {reportData.length === 0
                      ? "Click Load Report to view LLO funnel tasks."
                      : "No tasks match the selected filters."}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRows.map((row) => {
                  const overdue = isOverdue(row.dueDate);
                  const cellSx = getRowCellSx(overdue);
                  const groupCellSx = { ...matterGroupCellStyle, ...cellSx };

                  return (
                  <TableRow
                    key={row.taskId}
                    hover
                    sx={{
                      "&:hover .MuiTableCell-root": {
                        bgcolor: overdue ? OVERDUE_ROW_HOVER_BG : "action.hover",
                      },
                    }}
                  >
                    {!row.skipCaseColumns ? (
                      <>
                        <TableCell rowSpan={row.groupRowSpan} sx={groupCellSx}>
                          {row.rowNumber}
                        </TableCell>
                        <TableCell
                          rowSpan={row.groupRowSpan}
                          sx={{
                            ...groupCellSx,
                            ...getBodyCellSx(HEAD_CELL_BY_ID.caseName, { fontWeight: 500 }),
                          }}
                        >
                          {row.caseName}
                        </TableCell>
                        <TableCell rowSpan={row.groupRowSpan} sx={{ ...groupCellSx, ...getBodyCellSx(HEAD_CELL_BY_ID.raa) }}>
                          {row.raa}
                        </TableCell>
                        <TableCell rowSpan={row.groupRowSpan} sx={{ ...groupCellSx, ...getBodyCellSx(HEAD_CELL_BY_ID.ap) }}>
                          {row.ap}
                        </TableCell>
                        <TableCell
                          rowSpan={row.groupRowSpan}
                          sx={{
                            ...groupCellSx,
                            ...getBodyCellSx(HEAD_CELL_BY_ID.upcomingDeadlines, { overflow: "hidden" }),
                          }}
                        >
                          <UpcomingDeadlinesCell text={row.upcomingDeadlines} />
                        </TableCell>
                        <TableCell
                          rowSpan={row.groupRowSpan}
                          sx={{ ...groupCellSx, ...getBodyCellSx(HEAD_CELL_BY_ID.lloExpiration, { whiteSpace: "nowrap" }) }}
                        >
                          {row.lloExpiration || "—"}
                        </TableCell>
                        <TableCell rowSpan={row.groupRowSpan} sx={{ ...groupCellSx, ...getBodyCellSx(HEAD_CELL_BY_ID.negotiation) }}>
                          {row.negotiation}
                        </TableCell>
                      </>
                    ) : null}
                    <TableCell sx={{ ...cellSx, ...getBodyCellSx(HEAD_CELL_BY_ID.funnel) }}>{row.funnel}</TableCell>
                    <TableCell
                      sx={{
                        ...cellSx,
                        ...getBodyCellSx(HEAD_CELL_BY_ID.dueDate, {
                          whiteSpace: "nowrap",
                          color: overdue ? "error.main" : "inherit",
                          fontWeight: overdue ? 600 : 400,
                        }),
                      }}
                    >
                      {row.dueDate ? dayjs(row.dueDate).format("MM/DD/YYYY") : "—"}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, ...getBodyCellSx(HEAD_CELL_BY_ID.taskName) }}>{row.taskName}</TableCell>
                    <TableCell sx={{ ...cellSx, ...getBodyCellSx(HEAD_CELL_BY_ID.taskAssignee) }}>{row.taskAssignee}</TableCell>
                    <TableCell sx={{ ...cellSx, ...getBodyCellSx(HEAD_CELL_BY_ID.description) }}>
                      <DescriptionCell text={row.description} />
                    </TableCell>
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider />
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={caseGroups.length}
          rowsPerPage={rowsPerPage}
          page={page}
          labelRowsPerPage="Cases per page"
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{ flexShrink: 0, minHeight: 52 }}
        />
      </Paper>
    </Box>
  );
}
